import { useState } from 'react';

import {
  BlindSpotMonitorModal,
} from '@/components/modals/blind-spot-monitor-modal/component';
import {
  EmergencyBrakeAssistModal,
} from '@/components/modals/emergency-brake-assist-modal';
import {
  HillStartAssistModal,
} from '@/components/modals/hill-start-assist-modal';
import {
  LaneDepartureWarningModal,
} from '@/components/modals/lane-departure-warning-modal';
import {
  LaneKeepAssistModal,
} from '@/components/modals/lane-keep-assist-modal';
import { VEHICLE_NAME_MPV } from '@/constants/global';

import { GroupSection } from '../group-section';
import {
  FEATURE_ITEM_360_VIEW_CAMERA,
  FEATURE_ITEM_ANDROID_AUTO,
  FEATURE_ITEM_APPLE_CARPLAY,
  FEATURE_ITEM_BLIND_SPOT_MONITOR,
  FEATURE_ITEM_BLUETOOTH,
  FEATURE_ITEM_CAPTAIN_SEAT,
  FEATURE_ITEM_CRUISE_CONTROL,
  FEATURE_ITEM_DRIVERS_ELECTRIC_SEAT,
  FEATURE_ITEM_ELECTRIC_PARK_BRAKE,
  FEATURE_ITEM_ELECTRIC_SLIDING_DOOR,
  FEATURE_ITEM_ELECTRIC_TAILGATE,
  FEATURE_ITEM_EMERGENCY_BRAKE_ASSIST,
  FEATURE_ITEM_FOLDING_TABLE,
  FEATURE_ITEM_HEADS_UP_DISPLAY,
  FEATURE_ITEM_HILL_START_ASSIST,
  FEATURE_ITEM_INFOYAINMENT,
  FEATURE_ITEM_ISOFIX,
  FEATURE_ITEM_KNOCKDOWN_REAR_SEAT,
  FEATURE_ITEM_LANE_DEPARTURE_WARNING,
  FEATURE_ITEM_LANE_KEEP_ASSIST,
  FEATURE_ITEM_NAVIGATION_SYSTEM,
  FEATURE_ITEM_NO_OF_AIRBAGS,
  FEATURE_ITEM_PARK_ASSIST,
  FEATURE_ITEM_PARKING_SENSORS,
  FEATURE_ITEM_PASSENGERS_ELECTRIC_SEAT,
  FEATURE_ITEM_REVERSE_CAMERA,
  FEATURE_ITEM_SOUND_SYSTEM,
  FEATURE_ITEM_TITLE_AUTO_HEAD_LIGHTS,
  FEATURE_ITEM_TITLE_DYNAMIC_RUNNING_LIGHTS,
  FEATURE_ITEM_TITLE_ELECTRICE_FOLDING_SIDE_MIRRORS,
  FEATURE_ITEM_TITLE_HEAD_LIGHTS,
  FEATURE_ITEM_TITLE_KEYLESS_ENGINE_START,
  FEATURE_ITEM_TITLE_KEYLESS_ENTRY,
  FEATURE_ITEM_TITLE_MULTI_FUNCTION_STEERING_WHEEL,
  FEATURE_ITEM_TITLE_MULTI_ZONE_AIRCON,
  FEATURE_ITEM_TITLE_PADDLE_SHIFTERS,
  FEATURE_ITEM_TITLE_RAIN_SENSING_WIPERS,
  FEATURE_ITEM_TITLE_REAR_AIRCON,
  FEATURE_ITEM_TITLE_SEAT_UPHOLSTERY,
  FEATURE_ITEM_TITLE_SUNROOF,
  FEATURE_ITEM_TYRE_PRESSURE_MONITOR,
  FEATURE_ITEM_VENTILATED_SEAT,
  FEATURE_ITEM_WIRELESS_SMARTPHONE_CHARGING,
  FEATURE_SUB_TITLE_ADDITIONAL_FEATURES,
  FEATURE_SUB_TITLE_BRAKES_AND_SUSPENSION,
  FEATURE_SUB_TITLE_COMFORT_AND_CONVENIENCE,
  FEATURE_SUB_TITLE_DRIVING_ASSISTANCE,
  FEATURE_SUB_TITLE_EXTERIOR,
  FEATURE_SUB_TITLE_INFOTAINMENT_SYSTEM,
  FEATURE_SUB_TITLE_INTERIOR,
  FEATURE_SUB_TITLE_LIGHTING,
  FEATURE_SUB_TITLE_REMARKS,
  FEATURE_SUB_TITLE_SAFETY_AND_LOCKS,
} from './constants';
import styles from './styles.module.scss';
import type { Component } from './types';
import {
  checkIsNullOrFalseOrZero,
  convertAirbags,
  convertValueToText,
  getConvertedValue,
} from './utils';

export const FeaturesInfoTable: Component = ({
  isAvailable,
  submodel,
  vehicleType,
}) => {
  const { features } = submodel;

  const [
    isHillStartAssistModalOpen,
    setIsHillStartAssistModalOpen,
  ] = useState(false);
  const [
    isEmergencyBrakeAssistModalOpen,
    setIsEmergencyBrakeAssistModalOpen,
  ] = useState(false);
  const [
    isLandKeepAssistModalOpen,
    setIsLandKeepAssistModalOpen,
  ] = useState(false);
  const [
    isLandDepartureWarningModalOpen,
    setIsLandDepartureWarningModalOpen,
  ] = useState(false);
  const [
    isBlindSideMonitorModalOpen,
    setIsBlindSideMonitorModalOpen,
  ] = useState(false);

  const isMPV = vehicleType === VEHICLE_NAME_MPV;

  const checkIsAvailableCarOrExistValue = (value: number | boolean | null | string): boolean => isAvailable // @suppress longLineCheck
    || !checkIsNullOrFalseOrZero(value);

  const handleHillStartAssistClick = () => {
    setIsHillStartAssistModalOpen(true);
  };

  const handleEmergencyBrakeAssistClick = () => {
    setIsEmergencyBrakeAssistModalOpen(true);
  };

  const handleLandKeepAssistClick = () => {
    setIsLandKeepAssistModalOpen(true);
  };

  const handleLandDepartureWarningClick = () => {
    setIsLandDepartureWarningModalOpen(true);
  };

  const handleBlindSideMonitorClick = () => {
    setIsBlindSideMonitorModalOpen(true);
  };

  const formatHtmlStringToArray = (featuresHtml: string | null): string[] => {
    if (!featuresHtml) return [];

    const parser = new DOMParser();
    const doc = parser.parseFromString(featuresHtml, 'text/html');
    const listItems = doc.querySelectorAll('ul > li');

    return Array.from(listItems).map(li => li.textContent?.trim() || '');
  };

  const groupedFeatures = [
    {
      title: FEATURE_SUB_TITLE_LIGHTING,
      items: [
        { title: FEATURE_ITEM_TITLE_HEAD_LIGHTS, value: convertValueToText(features.headLights), show: true }, // @suppress longLineCheck
        { title: FEATURE_ITEM_TITLE_DYNAMIC_RUNNING_LIGHTS, value: convertValueToText(features.daytimeRunningLights), show: true }, // @suppress longLineCheck
        { title: FEATURE_ITEM_TITLE_AUTO_HEAD_LIGHTS, value: convertValueToText(features.autoHeadlights), show: true }, // @suppress longLineCheck
      ],
    },
    {
      title: FEATURE_SUB_TITLE_EXTERIOR,
      items: [
        { title: FEATURE_ITEM_TITLE_ELECTRICE_FOLDING_SIDE_MIRRORS, value: convertValueToText(features.autoRetractableSideMirrors), show: true }, // @suppress longLineCheck
        { title: FEATURE_ITEM_TITLE_RAIN_SENSING_WIPERS, value: convertValueToText(features.rainSensingWipers), show: true }, // @suppress longLineCheck
      ],
    },
    {
      title: FEATURE_SUB_TITLE_INTERIOR,
      items: [
        { title: FEATURE_ITEM_TITLE_MULTI_FUNCTION_STEERING_WHEEL, value: convertValueToText(features.controlOnSteeringWheel), show: true }, // @suppress longLineCheck
        { title: FEATURE_ITEM_TITLE_PADDLE_SHIFTERS, value: convertValueToText(features.paddleShifters), show: true }, // @suppress longLineCheck
        { title: FEATURE_ITEM_TITLE_KEYLESS_ENTRY, value: convertValueToText(features.smartKey), show: true }, // @suppress longLineCheck
        { title: FEATURE_ITEM_TITLE_KEYLESS_ENGINE_START, value: convertValueToText(features.pushStartEngine), show: true }, // @suppress longLineCheck
        { title: FEATURE_ITEM_TITLE_MULTI_ZONE_AIRCON, value: convertValueToText(features.dualZoneAc), show: true }, // @suppress longLineCheck
        { title: FEATURE_ITEM_TITLE_REAR_AIRCON, value: convertValueToText(features.rearAircon), show: true }, // @suppress longLineCheck
        { title: FEATURE_ITEM_TITLE_SEAT_UPHOLSTERY, value: convertValueToText(features.seatUpholstery), show: checkIsAvailableCarOrExistValue(features.seatUpholstery) }, // @suppress longLineCheck
        { title: FEATURE_ITEM_TITLE_SUNROOF, value: convertValueToText(features.sunroofMoonroofSkyroof), show: true }, // @suppress longLineCheck
      ],
    },
    {
      title: FEATURE_SUB_TITLE_COMFORT_AND_CONVENIENCE,
      items: [
        { title: FEATURE_ITEM_CAPTAIN_SEAT, value: convertValueToText(features.captainSeat), show: isMPV && checkIsAvailableCarOrExistValue(features.captainSeat) }, // @suppress longLineCheck
        { title: FEATURE_ITEM_FOLDING_TABLE, value: convertValueToText(features.foldingTable), show: isMPV && checkIsAvailableCarOrExistValue(features.foldingTable) }, // @suppress longLineCheck
        { title: FEATURE_ITEM_DRIVERS_ELECTRIC_SEAT, value: getConvertedValue('driverElectricSeats', features.driverElectricSeat), show: true }, // @suppress longLineCheck
        { title: FEATURE_ITEM_PASSENGERS_ELECTRIC_SEAT, value: getConvertedValue('driverElectricSeats', features.passengerElectricSeat), show: checkIsAvailableCarOrExistValue(features.passengerElectricSeat) }, // @suppress longLineCheck
        { title: FEATURE_ITEM_VENTILATED_SEAT, value: getConvertedValue('ventilatedSeats', features.ventilatedSeats), show: checkIsAvailableCarOrExistValue(features.ventilatedSeats) }, // @suppress longLineCheck
        { title: FEATURE_ITEM_KNOCKDOWN_REAR_SEAT, value: convertValueToText(features.knockdownRearSeats), show: true }, // @suppress longLineCheck
        { title: FEATURE_ITEM_ELECTRIC_TAILGATE, value: convertValueToText(features.electricTailgate), show: true }, // @suppress longLineCheck
        { title: FEATURE_ITEM_ELECTRIC_SLIDING_DOOR, value: getConvertedValue('electricSlidingDoor', features.electricSlidingDoor), show: isMPV && checkIsAvailableCarOrExistValue(features.electricSlidingDoor) }, // @suppress longLineCheck
      ],
    },
    {
      title: FEATURE_SUB_TITLE_INFOTAINMENT_SYSTEM,
      items: [
        { title: FEATURE_ITEM_BLUETOOTH, value: convertValueToText(features.bluetoothInterface), show: true }, // @suppress longLineCheck
        { title: FEATURE_ITEM_APPLE_CARPLAY, value: getConvertedValue('carPlay', features.appleCarPlay), show: checkIsAvailableCarOrExistValue(features.appleCarPlay) }, // @suppress longLineCheck
        { title: FEATURE_ITEM_ANDROID_AUTO, value: getConvertedValue('carPlay', features.androidAuto), show: checkIsAvailableCarOrExistValue(features.androidAuto) }, // @suppress longLineCheck
        { title: FEATURE_ITEM_WIRELESS_SMARTPHONE_CHARGING, value: convertValueToText(features.wirelessSmartPhoneCharging), show: checkIsAvailableCarOrExistValue(features.wirelessSmartPhoneCharging) }, // @suppress longLineCheck
        { title: FEATURE_ITEM_NAVIGATION_SYSTEM, value: convertValueToText(features.navigationSystem), show: true }, // @suppress longLineCheck
        { title: FEATURE_ITEM_HEADS_UP_DISPLAY, value: convertValueToText(features.headUpDisplay), show: checkIsAvailableCarOrExistValue(features.headUpDisplay) }, // @suppress longLineCheck
        { title: FEATURE_ITEM_INFOYAINMENT, value: convertValueToText(features.touchscreenDisplay), show: checkIsAvailableCarOrExistValue(features.touchscreenDisplay) }, // @suppress longLineCheck
        { title: FEATURE_ITEM_SOUND_SYSTEM, value: convertValueToText(features.soundSystem), show: checkIsAvailableCarOrExistValue(features.soundSystem) }, // @suppress longLineCheck
      ],
    },
    {
      title: FEATURE_SUB_TITLE_SAFETY_AND_LOCKS,
      items: [
        { title: FEATURE_ITEM_NO_OF_AIRBAGS, value: convertAirbags(features.airbags), show: true }, // @suppress longLineCheck
        { title: FEATURE_ITEM_ISOFIX, value: convertValueToText(features.isofix), show: checkIsAvailableCarOrExistValue(features.isofix) }, // @suppress longLineCheck
        { title: FEATURE_ITEM_TYRE_PRESSURE_MONITOR, value: convertValueToText(features.tyrePressureMonitor), show: checkIsAvailableCarOrExistValue(features.tyrePressureMonitor) }, // @suppress longLineCheck
      ],
    },
    {
      title: FEATURE_SUB_TITLE_BRAKES_AND_SUSPENSION,
      items: [
        { title: FEATURE_ITEM_ELECTRIC_PARK_BRAKE, value: getConvertedValue('electricParkBrake', features.electricParkBrakeButton), show: true }, // @suppress longLineCheck
        {
          title: FEATURE_ITEM_HILL_START_ASSIST, value: convertValueToText(features.hillStartAssist), show: checkIsAvailableCarOrExistValue(features.hillStartAssist), hasDesc: true, onClickDesc: handleHillStartAssistClick, // @suppress longLineCheck
        },
        {
          title: FEATURE_ITEM_EMERGENCY_BRAKE_ASSIST, value: convertValueToText(features.emcyBrakeAssist), show: checkIsAvailableCarOrExistValue(features.emcyBrakeAssist), hasDesc: true, onClickDesc: handleEmergencyBrakeAssistClick, // @suppress longLineCheck
        },
        { title: FEATURE_ITEM_PARKING_SENSORS, value: getConvertedValue('parkingSensors', features.reverseSensors), show: checkIsAvailableCarOrExistValue(features.reverseSensors) }, // @suppress longLineCheck
        { title: FEATURE_ITEM_REVERSE_CAMERA, value: getConvertedValue('reverseCamera', features.reverseCamera), show: true }, // @suppress longLineCheck
        { title: FEATURE_ITEM_360_VIEW_CAMERA, value: convertValueToText(features.viewCamera), show: checkIsAvailableCarOrExistValue(features.viewCamera) }, // @suppress longLineCheck
        { title: FEATURE_ITEM_PARK_ASSIST, value: convertValueToText(features.parkAssist), show: checkIsAvailableCarOrExistValue(features.parkAssist) }, // @suppress longLineCheck
      ],
    },
    {
      title: FEATURE_SUB_TITLE_DRIVING_ASSISTANCE,
      items: [
        { title: FEATURE_ITEM_CRUISE_CONTROL, value: getConvertedValue('cruiseControl', features.cruiseControl), show: true }, // @suppress longLineCheck
        {
          title: FEATURE_ITEM_LANE_KEEP_ASSIST, value: convertValueToText(features.laneKeepAssist), show: checkIsAvailableCarOrExistValue(features.laneKeepAssist), hasDesc: true, onClickDesc: handleLandKeepAssistClick, // @suppress longLineCheck
        },
        {
          title: FEATURE_ITEM_LANE_DEPARTURE_WARNING, value: convertValueToText(features.laneDepWarning), show: checkIsAvailableCarOrExistValue(features.laneDepWarning), hasDesc: true, onClickDesc: handleLandDepartureWarningClick, // @suppress longLineCheck
        },
        {
          title: FEATURE_ITEM_BLIND_SPOT_MONITOR, value: getConvertedValue('blindSpotMonitor', features.blindSpotMonitor), show: checkIsAvailableCarOrExistValue(features.blindSpotMonitor), hasDesc: true, onClickDesc: handleBlindSideMonitorClick, // @suppress longLineCheck
        },
      ],
    },
  ];

  const hasAdditionalFeatures = features.additionalFeatures && features.additionalFeatures.length !== 0; // @suppress longLineCheck
  const hasRemarks = features.remarks && features.remarks.length !== 0;
  const formattedAdditionalFeatures = formatHtmlStringToArray(features.additionalFeatures); // @suppress longLineCheck
  const formattedRemarks = formatHtmlStringToArray(features.remarks);

  return (
    <div className={styles.containerRoot}>
      <HillStartAssistModal
        isOpen={isHillStartAssistModalOpen}
        onClose={() => {
          setIsHillStartAssistModalOpen(false);
        }} />
      <EmergencyBrakeAssistModal
        isOpen={isEmergencyBrakeAssistModalOpen}
        onClose={() => {
          setIsEmergencyBrakeAssistModalOpen(false);
        }} />
      <LaneKeepAssistModal
        isOpen={isLandKeepAssistModalOpen}
        onClose={() => {
          setIsLandKeepAssistModalOpen(false);
        }} />
      <LaneDepartureWarningModal
        isOpen={isLandDepartureWarningModalOpen}
        onClose={() => {
          setIsLandDepartureWarningModalOpen(false);
        }} />
      <BlindSpotMonitorModal
        isOpen={isBlindSideMonitorModalOpen}
        onClose={() => {
          setIsBlindSideMonitorModalOpen(false);
        }} />
      {groupedFeatures.map(group => (
        <GroupSection
          key={group.title}
          items={group.items}
          title={group.title} />
      ))}
      <div className={styles.containerSubSection}>
        {hasAdditionalFeatures && (
          <>
            <div className={styles.textSubTitle}>
              {FEATURE_SUB_TITLE_ADDITIONAL_FEATURES}
            </div>
            <div className={styles.containerSubItems}>
              <ul className={styles.containerListContent}>
                {formattedAdditionalFeatures.map(feature => (
                  <li
                    key={`additional-feature-${feature}`}
                    className={styles.textListContent}>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
        {hasRemarks && (
          <>
            <div className={styles.textSubTitle}>
              {FEATURE_SUB_TITLE_REMARKS}
            </div>
            <div className={styles.containerSubItems}>
              <ul className={styles.containerListContent}>
                {formattedRemarks.map(remark => (
                  <li
                    key={`remark-${remark}`}
                    className={styles.textListContent}>
                    {remark}
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
